<template>
  <div class="creator-program-billing-account-form">
    <slot name="beforeForm" />

    <form novalidate @submit.prevent="submit">
      <v-text-field
        v-model="externalID"
        :label="$t('microsoft_business_central_id')"
      />

      <v-text-field
        v-model="companyNumber"
        :label="$t('company_number')"
      />

      <v-text-field
        v-model="name"
        :label="$t('legal_business_name_label')"
        :error-messages="nameErrors"
        required
        :readonly="isDisabled"
        @blur="v$.name.$touch()"
      />

      <magic-container>
        <v-autocomplete
          v-model="country"
          :items="countries"
          :label="$t('country_label')"
          item-title="name"
          item-value="code"
          prepend-inner-icon="public"
          class="country-dropdown"
          :error-messages="countryErrors"
          return-object
          required
          :hide-details="false"
          :readonly="isDisabled"
          @blur="v$.country.$touch()"
        />

        <v-autocomplete
          v-model="business"
          :items="businesses"
          prepend-inner-icon="home"
          item-value="id"
          item-title="name"
          :label="$t('business_label')"
          @update:search="businessSearch"
          :error-messages="businessErrors"
          return-object
          :hide-details="false"
          :readonly="isDisabled"
          :loading="isLoadingBusinesses"
          @focus="onBusinessFocus"
        />
      </magic-container>

      <magic-container>
        <v-text-field
          v-model="address"
          :label="$t('address_line_one_label')"
          required
          type="text"
          :error-messages="addressErrors"
          :readonly="isDisabled"
          @blur="v$.address.$touch()"
        />

        <v-text-field
          v-model="addressOpt"
          :label="$t('address_line_two_label')"
          :readonly="isDisabled"
        />
      </magic-container>

      <magic-container>
        <v-text-field
          v-model="city"
          :label="$t('city_label')"
          type="text"
          :error-messages="cityErrors"
          required
          :readonly="isDisabled"
          @blur="v$.city.$touch()"
        />

        <v-text-field
          v-model="zip"
          :label="$t('zip_code_label')"
          type="text"
          required
          :readonly="isDisabled"
          :error-messages="zipErrors"
          @blur="v$.zip.$touch()"
        />
      </magic-container>

      <forms-billing-account-vat
        v-model="vat"
        ref="vatForm"
        :country="country"
        :is-disabled="isDisabled"
        :on-change-not-vat-registered="v => notVatRegistered = v"
        :is-ckeckbox-disabled="areCheckBoxesDisabled"
      />

      <magic-container>
        <v-text-field
          v-model="taxNumber"
          :label="$t('tax_number')"
          type="text"
          :readonly="isDisabled"
        />

        <v-checkbox
          v-model="isSmallBusinessOwner"
          density="compact"
          :label="$t('small_business_owner')"
          :readonly="isDisabled"
          :disabled="areCheckBoxesDisabled"
        />
      </magic-container>

      <v-text-field
        v-model="iban"
        :label="$t('account_number_iban')"
        type="text"
        required
        :readonly="isDisabled"
        :error-messages="ibanErrors"
        @blur="v$.iban.$touch()"
      />

      <magic-container>
        <v-text-field
          v-model="bankName"
          :label="$t('bank_name')"
          type="text"
          required
          :readonly="isDisabled"
          :error-messages="bankNameErrors"
          @blur="v$.bankName.$touch()"
        />

        <v-text-field
          v-model="bic"
          :label="$t('sort_code_bic')"
          type="text"
          required
          :readonly="isDisabled"
          :error-messages="bicErrors"
          @blur="v$.bic.$touch()"
        />
      </magic-container>

      <forms-billing-emails
        :emails="emails"
        :is-disabled="isDisabled"
        :on-submit="changeEmails"
        class="mb-6"
      />

      <div
        class="form-actions"
      >
        <v-btn
          class="trigger"
          variant="text"
          @click="cancel"
        >
          Cancel
        </v-btn>

        <v-btn
          v-if="!isDisabled"
          color="primary"
          class="trigger"
          variant="flat"
          :loading="isLoading"
          type="submit"
        >
          {{ confirmTriggerLabel }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators'
  import { isObj, isNum, isStr, debounce } from '@/utils';
  import fetchIfNotInStoreMixin from '@/mixins/fetchIfNotInStore';

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    emits: ['saved'],
    mixins: [fetchIfNotInStoreMixin],
    setup () {
      return { v$: useVuelidate() }
    },
    validations() {
      return {
        name: {
          required
        },
        business: {
          required
        },
        address: {
          required
        },
        city: {
          required
        },
        country: {
          required
        },
        zip: {
          required
        },
        iban: {
          required
        },
        bankName: {
          required
        },
        bic: {
          required
        },
      };
    },
    props: {
      isDisabled: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        id: null,
        accountingCountryErrors: [],
        companyNumber: '',
        externalID: '',
        business: null,
        businesses: [],
        name: '',
        address: '',
        addressOpt: '',
        city: '',
        zip: '',
        country: null,
        vat: '',
        emails: [],
        iban: '',
        bankName: '',
        bic: '',
        isLoading: false,
        isLoadingBusinesses: false,
        isLoadingManagers: false,
        isRefreshingBusiness: false,
        notVatRegistered: false,
        isSmallBusinessOwner: false,
        taxNumber: '',
      };
    },
    computed: {
      areCheckBoxesDisabled() {
        return this.country?.code === 'PL' || this.country?.code === 'NL';
      },
      confirmTriggerLabel() {
        return this.hasItem ? 'Update account' : 'Create account';
      },
      countries() {
        return this.$store.getters['config/countries'];
      },
      hasItem() {
        return isNum(this.id);
      },
      zipErrors() {
        return this.v$.zip.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'ZIP code is required';
          }
          return error.$message;
        });
      },
      nameErrors() {
        return this.v$.name.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Legal business name is required';
          }
          return error.$message;
        });
      },
      addressErrors() {
        return this.v$.address.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Address is required';
          }
          return error.$message;
        });
      },
      businessErrors() {
        return this.v$.business.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Business is required';
          }
          return error.$message;
        });
      },
      cityErrors() {
        return this.v$.city.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'City is required';
          }
          return error.$message;
        });
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
      ibanErrors() {
        return this.v$.iban.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'IBAN is required';
          }
          return error.$message;
        });
      },
      bankNameErrors() {
        return this.v$.bankName.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Bank name is required';
          }
          return error.$message;
        });
      },
      bicErrors() {
        return this.v$.bic.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'BIC is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      '$route.params.id': {
        handler(nextVal) {
          if (nextVal) {
            this.fetchIfNotInStore(nextVal,'creatorProgram', 'billingAccounts', 'getBillingAccount').then((data) => {
              this.clear();
              this.onReceiveItem(data);
            }, () => this.leaveMissingItem(nextVal));
          }
        },
        deep: true,
        immediate: true,
      },
      business() {
        this.isRefreshingBusiness = true;
      },
      areCheckBoxesDisabled(val) {
        if (val) {
          this.isSmallBusinessOwner = false;
        }
      },
    },
    methods: {
      onReceiveItem(nextItem) {
        if (isObj(nextItem) && isNum(nextItem.id)) {
          this.extractItemData(nextItem);
        }
      },
      cancel() {
        this.clear();
        this.$router.push({ name: 'creator-program-billing-accounts', query: this.$route.query });
      },
      submit() {
        if (this.isLoading) return;

        this.v$.$touch();
        this.$refs.vatForm.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.sumbitItem();
      },
      clear() {
        this.v$.$reset();
        this.externalID = '';
        this.companyNumber = '';
        this.name = '';
        this.business = null;
        this.address = '';
        this.addressOpt = '';
        this.city = '';
        this.zip = '';
        this.country = null;
        this.vat = '';
        this.$refs.vatForm.originalVat = 'XXXX';
        this.emails = [];
        this.bic = '';
        this.bankName = '';
        this.iban = '';
        this.isRefreshingBusiness = false;
        this.isSmallBusinessOwner = false;
        this.taxNumber = '';
      },
      sumbitItem() {
        const {
          id, companyNumber, name, business, address, addressOpt, city,
          zip, country, vat, emails, bankName, bic, iban, notVatRegistered, isSmallBusinessOwner, taxNumber } = this;

        this.isLoading = true;

        const item = {
          id,
          external_id: this.externalID,
          company_number: companyNumber,
          legal_business_name: name,
          loyalty_program_business_id: business.id,
          address,
          address_opt: addressOpt,
          city,
          zip,
          country: country.code,
          vat_id: notVatRegistered ? '' : vat,
          invoice_emails: emails.map(e => e.email),
          bank_name: bankName,
          bic,
          iban,
          is_vat_registered: notVatRegistered ? 0 : 1,
          is_small_business: isSmallBusinessOwner ? 1 : 0,
          tax_number: taxNumber
        };

        const action = this.hasItem ? 'creatorProgram/updateBillingAccount' : 'creatorProgram/createBillingAccount';

        return this.$store.dispatch(action, item)
          .then(() => {
            this.clear();

            this.$emit('saved');
            this.$router.push({ name: 'creator-program-billing-accounts', query: this.$route.query });
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
      canSubmitRequest() {
        const {
          name, address, bankName, bic, iban,
          city, country, business } = this;

        const isValidVat = this.$refs.vatForm.isValid;

        return name !== ''
          && isNum(business?.id)
          && address !== ''
          && city !== ''
          && isStr(country?.code)
          && bankName !== ''
          && bic !== ''
          && iban !== ''
          && isValidVat;
      },
      extractItemData(item) {
        const {
          id, company_number, address, address_opt, legal_business_name, external_id,
          business, invoice_emails, city, zip, vat_id, is_vat_registered, country,
          linked_countries, bic, iban, bank_name, is_small_business, tax_number } = item;

        this.id = id;
        this.externalID = external_id;
        this.companyNumber = company_number;
        this.address = address;
        this.name = legal_business_name;
        this.city = city;
        this.zip = zip;
        this.country = country;
        this.linkedCountries = linked_countries;
        this.vat = vat_id;
        this.$refs.vatForm.originalVat = vat_id;
        this.bic = bic;
        this.iban = iban;
        this.bankName = bank_name;
        this.emails = invoice_emails;
        this.notVatRegistered = is_vat_registered === 0;
        this.$refs.vatForm.notVatRegistered = is_vat_registered === 0;
        this.isSmallBusinessOwner = is_small_business === 1;
        this.taxNumber = tax_number;

        if (address_opt) {
          this.addressOpt = address_opt;
        }

        if (isObj(business)) {
          this.business = business;
          this.getBusinesses();
          this.isRefreshingBusiness = true;
        }
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusiness) {
            this.isRefreshingBusiness = false;
            return false;
          }

          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      onBusinessFocus() {
        if (!this.businesses.length) {
          this.getBusinesses();
        }
      },
      getBusinesses(search) {
        const query = {
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store.dispatch('creatorProgram/getBusinesses', query)
          .then(({ businesses }) => {
            if (this.business?.id) {
              this.businesses = [this.business, ...businesses.filter(b => b.id !== this.business.id)];
            } else {
              this.businesses = businesses;
            }
            this.isLoadingBusinesses = false;
            return businesses;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      changeEmails(emails) {
        this.emails = emails;

        if (this.hasItem) {
          const query = {
            id: this.id,
            invoice_emails: emails.map(e => e.email)
          };

          return this.$store.dispatch('billing/updateInvoiceEmails', query);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/theme' as *;

.creator-program-billing-account-form {
  padding: $spacing-xl;

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 4px 0 0 0;

    .v-btn {
      margin: 0 $spacing-sm 0 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
